import React, { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faPhoneAlt } from "@fortawesome/free-solid-svg-icons";
import { motion, useScroll, useTransform } from "framer-motion";

import NavbarLogo from "../components/Navbar/JettyEstateLogo.png";
import leafBg from "../assets/jettymayfair/leaf_dew_drops.png";
import directorImg from "../assets/jettymayfair/director_msg_img.jpg";
import gatewayBg from "../assets/jettymayfair/gateway_bg.jpg";

import page5Left from "../assets/jettymayfair/page5_left.jpg";
import page5Right from "../assets/jettymayfair/page5_right.jpg";
import page6Render from "../assets/jettymayfair/page6_render.jpg";
import page7Render from "../assets/jettymayfair/page7_render.jpg";
import page8 from "../assets/jettymayfair/page_08.jpg";
import page9Render from "../assets/jettymayfair/page9_render.jpg";
import page10Club from "../assets/jettymayfair/page10_clubhouse.jpg";
import page10Col from "../assets/jettymayfair/page10_collage.jpg";
import page11 from "../assets/jettymayfair/page_11.jpg";

const Jettymayfair = () => {
  const section2Ref = useRef(null);
  const { scrollYProgress: section2ScrollY } = useScroll({
    target: section2Ref,
    offset: ["start end", "start start"]
  });

  const section2BorderRadius = useTransform(
    section2ScrollY,
    [0, 0.8, 1],
    ["1000px", "1000px", "0px"]
  );

  const section2Y = useTransform(
    section2ScrollY,
    [0, 1],
    [120, 0]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full min-h-screen bg-[#FDF5E6]">
      {/* SECTION 1 - Designed */}
      <section className="sticky top-0 h-screen w-full flex flex-col items-center justify-center z-10 bg-[#FFF7E8]">
        <div className="flex flex-col items-center justify-center text-center -mt-20">
          <h1 className="text-4xl md:text-6xl lg:text-8xl font-black text-[#0077b6] tracking-tight uppercase mb-2">
            Jetty Mayfair
          </h1>
          <p className="text-sm md:text-lg text-gray-700 tracking-[0.3em] font-medium uppercase">
            @ Rajamahendravaram
          </p>
        </div>

        <div className="absolute bottom-12 flex flex-col items-center justify-center">
          <p className="text-xs text-gray-800 tracking-widest font-bold mb-4 uppercase">Project By</p>
          <img src={NavbarLogo} alt="Jetty Estates" className="h-16 md:h-24 object-contain mb-2" />
          <p className="text-[#c69c6d] text-sm tracking-widest font-serif uppercase">Jetty Estates</p>
        </div>
      </section>

      {/* SECTION 2 - Designed */}
      <div ref={section2Ref} className="relative z-20 -mt-20">
        <motion.div 
          style={{
            y: section2Y,
            borderTopLeftRadius: section2BorderRadius,
            borderTopRightRadius: section2BorderRadius,
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${leafBg})`
          }}
          className="w-full h-screen flex flex-col items-center justify-center relative bg-black bg-cover bg-center bg-no-repeat shadow-2xl overflow-hidden"
        >
          <div className="flex flex-col items-center justify-center text-center p-8 max-w-4xl z-10">
            <h2 className="text-3xl md:text-5xl lg:text-6xl text-[#00a8e8] font-serif italic mb-16">
              Escape the noise. Embrace the view
            </h2>
            
            <div className="flex flex-col items-center mt-12">
              <h3 className="text-2xl md:text-4xl text-[#00a8e8] font-serif italic mb-6">
                Be Happy
              </h3>
              <p className="text-white text-sm md:text-lg tracking-[0.2em] uppercase font-light leading-loose">
                FOR THIS MOMENT.<br />
                THIS MOMENT IS OUR LIFE...<br />
                <span className="opacity-80">...WE PROMISE</span><br />
                <span className="font-bold">FOR YOUR HAPPINESS</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="relative z-20 bg-[#FDF5E6]">
        {/* SECTION 3 - Designed */}
        <section className="w-full min-h-screen flex flex-col md:flex-row bg-white">
        <div className="w-full md:w-1/2 p-12 md:p-24 flex flex-col justify-center relative">
          
          <div className="absolute top-12 right-12 opacity-50">
            <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M50 0C50 27.6142 72.3858 50 100 50C72.3858 50 50 72.3858 50 100C50 72.3858 27.6142 50 0 50C27.6142 50 50 27.6142 50 0Z" fill="#FDE68A" />
              <path d="M85 10C85 18.2843 91.7157 25 100 25C91.7157 25 85 31.7157 85 40C85 31.7157 78.2843 25 70 25C78.2843 25 85 18.2843 85 10Z" fill="#FDE68A" />
            </svg>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl text-[#00a8e8] font-serif italic mb-10">
            Director Message
          </h2>
          
          <div className="text-gray-600 space-y-6 text-sm md:text-base leading-relaxed max-w-xl">
            <p>
              Investing in land is not just a financial decision it is a commitment to the future. Land has always been a timeless asset, offering stability, growth, and lasting value. In an ever-changing world, land remains one of the few investments that stand the test of time.
            </p>
            <p>
              As we look ahead, our focus remains on creating value through strategic land development that aligns with sustainable practices and long-term growth. With careful planning and responsible investment, we aim to transform potential into prosperity not just for our investors, but for communities and future generations.
            </p>
            <p>
              We thank you for your continued trust and support as we journey together toward a future built on strong foundations.
            </p>
          </div>
          
          <div className="mt-12 text-gray-800">
            <p className="text-sm text-gray-500 mb-1">Warm Regards,</p>
            <p className="font-bold text-lg">Ravikanth Jetty</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Managing Director</p>
          </div>
        </div>
        
        <div className="w-full md:w-1/2 h-[50vh] md:h-screen">
          <img src={directorImg} alt="Director Message" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* SECTION 4 - Designed */}
      <section 
        className="w-full h-screen flex flex-col items-center justify-center relative bg-white bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${gatewayBg})` }}
      >
        <div className="flex flex-col items-center justify-center text-center p-8 max-w-4xl z-10">
          <p className="text-gray-600 tracking-[0.2em] uppercase font-bold text-sm mb-2">
            Enter the world of
          </p>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-[#0077b6] tracking-tighter uppercase mb-12">
            Jetty Mayfair
          </h2>
          
          <div className="bg-white/70 backdrop-blur-md p-8 md:p-12 rounded-3xl shadow-xl border border-white">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 uppercase tracking-widest mb-2">
              About the Project
            </h3>
            <p className="text-[#0077b6] font-medium mb-6">
              Luxury Living Near Rajamahendravaram!
            </p>
            
            <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-4">
              Discover an amazing villa plot layout designed for happiness, comfort, and a premium lifestyle! Nestled in a serene location near Rajamahendravaram, this gated community offers world-class amenities, lush green surroundings, and the perfect blend of modern living and natural beauty.
            </p>
            <p className="text-gray-700 text-sm md:text-base leading-relaxed">
              This thoughtfully planned community blends modern amenities with nature, ensuring a perfect living experience for you and your family.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5 - Quality & Villas */}
      <section className="w-full min-h-screen flex flex-col md:flex-row bg-white">
        <div className="w-full md:w-1/2 p-12 md:p-24 flex flex-col items-center justify-center text-center">
          <h2 className="text-3xl md:text-5xl text-[#00a8e8] font-serif italic mb-2">
            Quality is never an accident
          </h2>
          <p className="text-gray-600 tracking-widest uppercase font-bold text-sm mb-16">
            IT IS THE RESULT @ JETTY MAYFAIR
          </p>
          <div className="w-full h-full max-h-[500px] overflow-hidden flex items-end">
              <img src={page5Left} alt="Happy Kids" className="w-full object-contain" />
          </div>
        </div>
        <div className="w-full md:w-1/2 h-[50vh] md:h-screen relative">
          <img src={page5Right} alt="Villa Exterior" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* SECTION 6 - Highlights */}
      <section className="w-full min-h-screen flex flex-col md:flex-row bg-white">
        <div className="w-full md:w-3/4 h-[50vh] md:h-screen relative">
          <img src={page6Render} alt="Pond View" className="w-full h-full object-cover" />
        </div>
        <div className="w-full md:w-1/4 p-8 flex flex-col justify-center">
          <p className="text-xs font-bold text-gray-800 tracking-widest uppercase mb-1">
            Highlights of the layout
          </p>
          <h2 className="text-3xl font-black text-[#0077b6] tracking-tighter uppercase mb-8">
            Jetty Mayfair
          </h2>
          <ul className="space-y-6 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-[#00a8e8] font-bold mt-1">•</span>
              <span>Encumbrance free – 100% encumbrance free land with clear title</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00a8e8] font-bold mt-1">•</span>
              <span>Approach and internal roads – CC roads with footpath and kerb on either side, central medians etc.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00a8e8] font-bold mt-1">•</span>
              <span>Power – dedicated electrical power supply with electrical lines and street lighting</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00a8e8] font-bold mt-1">•</span>
              <span>Water supply – Water supply distribution network</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00a8e8] font-bold mt-1">•</span>
              <span>Sewerage – Sewerage pipe line network for the all the plots</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00a8e8] font-bold mt-1">•</span>
              <span>Greenery and Landscaping – Avenue Plantation, Greenery & landscaping in all open spaces and parks</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#00a8e8] font-bold mt-1">•</span>
              <span>Plots with Multipurpose use zone, sizes suitable for Villas / Building for various users</span>
            </li>
          </ul>
        </div>
      </section>

      {/* SECTION 7 - Amenities */}
      <section className="w-full min-h-screen flex flex-col-reverse md:flex-row bg-[#E6E8EA]">
        <div className="w-full md:w-1/4 p-8 overflow-y-auto max-h-screen bg-white">
          <h3 className="text-xl font-bold text-gray-800 mb-6 uppercase tracking-wider">Amenities</h3>
          <ul className="text-[10px] sm:text-xs text-gray-600 space-y-2 columns-2">
            <li>1. Grand Entry</li>
            <li>2. Security Check Post</li>
            <li>3. Security Room</li>
            <li>4. Vehicle Scanner</li>
            <li>5. Waterfall Main Entrance</li>
            <li>6. Basketball Court</li>
            <li>7. Children Play Area</li>
            <li>8. Walking Track</li>
            <li>9. Koi Pond</li>
            <li>10. Water Features</li>
            <li>11. Meditation Zone</li>
            <li>12. Sit Outs</li>
            <li>13. Jogging Track</li>
            <li>14. Golf</li>
            <li>15. Amphitheatre</li>
            <li>16. Out Door Fitness</li>
            <li>17. Floating Hangout</li>
            <li>18. Seating Deck</li>
            <li>19. Box Cricket</li>
            <li>20. Tennis Court</li>
            <li>21. Temple</li>
            <li>22. Sculpture-1</li>
            <li>23. Sculpture-2</li>
            <li>24. Sculpture-3</li>
            <li>25. Tree House</li>
            <li>26. Volley Ball</li>
            <li>27. Bonfire Pit</li>
            <li>28. Picnic Spot</li>
            <li>29. Ludo & Chess</li>
            <li>30. Landscaper</li>
            <li>31. Duck Pond</li>
            <li>32. Rabbit Area</li>
            <li>33. Lotus Pond</li>
            <li>34. Hammocks</li>
            <li>35. Rain Dance</li>
            <li>36. Swimming Pool</li>
            <li>37. Children Pool</li>
            <li>38. Open Lawn Area</li>
            <li>39. Lawn Sit Outs</li>
            <li>40. Party Lawn</li>
            <li>41. Open Function Area</li>
            <li>42. Outdoor Restroom</li>
            <li>43. Secured Entrance</li>
            <li>44. Private Theatre</li>
            <li>45. Gym</li>
            <li>46. Function Hall</li>
            <li>47. Coffee Shop</li>
            <li>48. E-Vehicles</li>
            <li>49. Senior Citizen Area</li>
            <li>50. Carroms</li>
            <li>51. Table Tennis</li>
            <li>52. Foosball table</li>
            <li>53. Snooker</li>
            <li>54. Stargazing Deck</li>
            <li>55. Footpath</li>
            <li>56. CC Roads</li>
            <li>57. Gazebo</li>
            <li>58. Streetlights</li>
            <li>59. Pathway Lights</li>
            <li>60. Fountains</li>
            <li>61. Garden</li>
            <li>62. Drop off Vehicles</li>
            <li>63. Boating Facility</li>
            <li>64. Fishing Deck</li>
            <li>65. CC cameras 360°</li>
            <li>66. Boardroom</li>
            <li>67. Super Market Place</li>
          </ul>
        </div>
        <div className="w-full md:w-3/4 h-[50vh] md:h-screen relative">
          <img src={page7Render} alt="Entrance View" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* SECTION 8 - Developments Collage (Full Image) */}
      <section className="w-full">
        <img src={page8} alt="Developments Collage" className="w-full h-auto block" loading="lazy" />
      </section>

      {/* SECTION 9 - Pool & Relax */}
      <section className="w-full min-h-screen flex flex-col md:flex-row bg-white">
        <div className="w-full md:w-1/3 p-12 md:p-24 flex flex-col items-center justify-center text-center">
          <p className="text-gray-800 tracking-widest uppercase font-bold text-xs mb-2">
            A HOME WITH LOVE & LAUGHTER
          </p>
          <p className="text-gray-600 tracking-widest uppercase font-medium text-xs mb-6">
            IS A TREASURE CHEST OF
          </p>
          <h2 className="text-5xl md:text-6xl text-[#00a8e8] font-serif italic">
            Happiness
          </h2>
        </div>
        <div className="w-full md:w-2/3 h-[50vh] md:h-screen relative">
          <img src={page9Render} alt="Pool View" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* SECTION 10 - Clubhouse */}
      <section className="w-full min-h-screen flex flex-col md:flex-row bg-white">
        <div className="w-full md:w-3/4 h-[50vh] md:h-screen relative">
          <img src={page10Club} alt="Clubhouse View" className="w-full h-full object-cover" />
        </div>
        <div className="w-full md:w-1/4 h-[50vh] md:h-screen bg-white flex items-center justify-center">
          <img src={page10Col} alt="Activities Collage" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* SECTION 11 - Master Plan */}
      <section className="w-full">
        <img src={page11} alt="Master Plan" className="w-full h-auto block" loading="lazy" />
      </section>

      {/* SECTION 12 - Outro */}
      <section className="w-full min-h-screen flex flex-col items-center justify-center relative bg-[#FDF5E6]">
        <div className="text-center max-w-4xl px-6">
          <h2 className="text-3xl md:text-5xl lg:text-6xl text-gray-600 font-medium leading-tight mb-20">
            <span className="text-[#c69c6d] text-4xl align-top mr-2">"</span>
            We take pride in being<br/>different & truly unique
            <span className="text-[#c69c6d] text-4xl align-top ml-2">"</span>
          </h2>
          
          <div className="flex flex-col items-center justify-center mb-16">
            <p className="text-sm text-gray-800 tracking-widest font-bold mb-4 uppercase">Landmark Project By</p>
            <img src={NavbarLogo} alt="Jetty Estates" className="h-20 md:h-24 object-contain mb-2" />
            <p className="text-[#c69c6d] text-sm tracking-widest font-serif uppercase">Jetty Estates</p>
          </div>
          
          <div className="border-t border-b border-gray-300 py-6 mb-6">
            <div className="flex flex-col md:flex-row items-center gap-6 justify-center">
              <p className="flex items-center gap-2 text-gray-700 text-sm">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                Jetty Mayfair, Beside Don Bosco School, Kondaguntur, Bommuru, Rajamahendravaram.
              </p>
              <div className="flex items-center gap-4 font-bold text-gray-800 text-sm md:text-lg">
                <span>Contact for More Information</span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faPhoneAlt} /> +91 98859 69292
                </span>
              </div>
            </div>
          </div>
          
          <p className="text-[10px] text-gray-500 max-w-2xl mx-auto">
            Note: This brochure is only a conceptual presentation and not a legal offering. The promoters reserve the right to alter and make changes in the layout & plans as deemed fit.
          </p>
        </div>
      </section>
      </div>
    </div>
  );
};

export default Jettymayfair;
